import type { EventContributionRules } from "./reward-rules.js";
import type { ActiveFactionLootBonus } from "./faction-loot-bonus.js";
import type { FactionId } from "./factions.js";
import type {
  RelayCaptureRules,
  RelayCaptureView,
  RELAY_PRESTIGE_ID,
} from "./relay.js";
import type { EquipmentKey, LegacyEquipmentKey } from "./arsenal.js";
import type { MissionObjective, NpcArchetype, SectorId } from "./contracts.js";
import type { StationTruceTerms } from "./station-truce.js";

export const SCHEDULED_PUBLIC_EVENT_TYPES = [
  "escort",
  "raid",
  "salvage",
  "boss",
  "relay",
] as const;
export const PUBLIC_EVENT_TYPES = [
  ...SCHEDULED_PUBLIC_EVENT_TYPES,
  "truce",
] as const;
export const ORION_ESCORT_PATH = [
  { x: 4080, y: 180 },
  { x: 4530, y: 180 },
  { x: 4900, y: 660 },
] as const;
export type PublicEventType = (typeof PUBLIC_EVENT_TYPES)[number];
export type ScheduledPublicEventType =
  (typeof SCHEDULED_PUBLIC_EVENT_TYPES)[number];
export type PublicEventOutcome = "success" | "failed";
export type PublicEventStatus = "available" | "active" | PublicEventOutcome;
export interface EventContribution {
  damage: number;
  healing: number;
  tasks: number;
}
export interface PublicEventParticipant {
  playerId: string;
  participationRunId: string;
  contribution: EventContribution;
}
export interface SalvageLayout {
  sites: { x: number; y: number; workSeconds: number }[];
  waves: NpcArchetype[][];
  mines: { x: number; y: number; radius: number; damage: number }[];
  clearanceRadius: number;
}
export interface PublicEventTerms extends PublicEventDefinition {
  version: "public-events-v2" | "public-events-v3" | "public-events-v4";
  truce?: { criminalId: string; rules: StationTruceTerms };
  contributionRules?: EventContributionRules;
  variantId: string;
  condition: string;
  salvage: SalvageLayout | null;
  relay?: RelayCaptureRules;
}
export interface PublicEventPresentation {
  prestigeReward?: typeof RELAY_PRESTIGE_ID;
  version: "public-events-v2" | "public-events-v3" | "public-events-v4";
  contributionRules?: EventContributionRules;
  variantId: string;
  title: string;
  description: string;
  condition: string;
  credits: string;
  components: number;
}
export interface PublicEventResolution {
  winnerFaction?: FactionId | null;
  eventId: string;
  eventType: PublicEventType;
  terms: PublicEventTerms;
  outcome: PublicEventOutcome;
  resolvedAt: number;
  participants: PublicEventParticipant[];
}
export interface PublicEventResult {
  winnerFaction?: FactionId | null;
  eventId: string;
  eventType: PublicEventType;
  terms: PublicEventPresentation | null;
  outcome: PublicEventOutcome;
  contribution: EventContribution;
  eligible: boolean;
  credits: string;
  components: number;
  blueprintUnlocks: EquipmentKey[];
  resolvedAt: number;
}
export interface PublicEventView {
  hasDeadline?: boolean;
  capture?: RelayCaptureView;
  id: string;
  type: PublicEventType;
  title: string;
  terms: PublicEventPresentation;
  workBlocked: boolean;
  sectorId: SectorId;
  status: PublicEventStatus;
  x: number;
  y: number;
  progress: number;
  goal: number;
  participants: number;
  scaledFor: number;
  remaining: number;
  objective: MissionObjective;
}
export interface PublicEventParticipation extends EventContribution {
  eventId: string;
  eligible: boolean;
  pending: boolean;
  interactionProgress: number;
}
export interface PublicEventsView {
  factionBonuses?: ActiveFactionLootBonus[];
  trackedEventId: string | null;
  participation: PublicEventParticipation[];
}
export interface PublicEventDefinition {
  type: PublicEventType;
  title: string;
  description: string;
  sectorId: SectorId;
  x: number;
  y: number;
  duration: number;
  credits: string;
  components: number;
  blueprintUnlocks: LegacyEquipmentKey[];
}
export const PUBLIC_EVENTS: Record<
  ScheduledPublicEventType,
  PublicEventDefinition
> = {
  escort: {
    type: "escort",
    title: "Конвой «Орион»",
    description:
      "Проведи движущийся транспорт через засады. Защищай, ремонтируй или держись рядом на маршруте.",
    sectorId: "wrecks",
    ...ORION_ESCORT_PATH[0],
    duration: 180,
    credits: "180",
    components: 4,
    blueprintUnlocks: [{ kind: "passive", id: "thrusters" }],
  },
  raid: {
    type: "raid",
    title: "Налёт на ретранслятор",
    description:
      "Отбей три волны, сохрани дружественный ретранслятор. Ремонт союзников учитывается.",
    sectorId: "wrecks",
    x: 3900,
    y: 1750,
    duration: 180,
    credits: "160",
    components: 4,
    blueprintUnlocks: [{ kind: "module", id: "interceptor" }],
  },
  salvage: {
    type: "salvage",
    title: "Разбор дрейфующих обломков",
    description:
      "Разбери три объекта среди мин и охраны. Подлети и запусти разбор; отход прерывает работу.",
    sectorId: "wrecks",
    x: 5650,
    y: 1810,
    duration: 180,
    credits: "140",
    components: 6,
    blueprintUnlocks: [{ kind: "module", id: "scan" }],
  },
  relay: {
    type: "relay",
    title: "Спорный ретранслятор",
    description:
      "Центральный узел связи. Победа даёт всей фракции +10% PvE-добычи на 30 минут. Удерживай зону своей фракцией; противник приостанавливает захват.",
    sectorId: "wrecks",
    x: 5350,
    y: 1150,
    duration: 240,
    credits: "240",
    components: 6,
    blueprintUnlocks: [],
  },
  boss: {
    type: "boss",
    title: "Разрушитель «Голиаф»",
    description:
      "Избегай обозначенных залпов и уничтожь крупный корабль в PvE-пограничье. Состав встречи зависит от числа пилотов.",
    sectorId: "wrecks",
    x: 6300,
    y: 400,
    duration: 240,
    credits: "300",
    components: 8,
    blueprintUnlocks: [
      { kind: "weapon", id: "rail" },
      { kind: "module", id: "repair-field" },
      { kind: "passive", id: "repair-drone" },
    ],
  },
};

export function eligibleEventContribution(
  contribution: EventContribution,
  rules: EventContributionRules,
): boolean {
  return (
    contribution.damage >= rules.damage ||
    contribution.healing >= rules.healing ||
    contribution.tasks >= rules.tasks
  );
}
